import { useState, useEffect, useRef } from 'react'
import type { MouseEvent } from 'react'
import { apiService } from '../services/apiService'
import { passwordCache } from '../utils/passwordCache'
import ShipmentItemRow, { SHIPMENT_TABLE_GRID } from './ShipmentItemRow'
import ShipmentChecklist from './ShipmentChecklist'
// import CUSMAIndicator from './CUSMAIndicator' // Hidden - CUSMA form filling no longer needed
// import CUSMAFormModal from './CUSMAFormModal' // Hidden - CUSMA form filling no longer needed
import InvoiceEditorDialog from './InvoiceEditorDialog'
import type { Shipment, ShipmentItem } from '../types'


interface ManufacturingAddress {
  id?: number
  name: string
  street_address: string
  city: string
  state?: string
  country: string
  zip_code?: string
  reasoning?: string
}

interface ShipmentDetailsProps {
  shipment: Shipment
  onBack: () => void
}

interface CatalogueFlagIssue {
  asin: string
  sku?: string
  description?: string
  catalogueUuid: string
  editUrl: string
  usFlagEnabled: boolean
  usFlagRequested: boolean
  missingFields: Array<{ field: string; label: string }>
  detailsAvailable: boolean
}

interface SSBHTSStatus {
  asin?: string
  description?: string
  rejectedHts?: string
  acceptedHts?: string
  status: 'rejected' | 'accepted' | 'applied' | 'pending'
  shipments: string[]
  message?: string
}

export default function ShipmentDetails({ shipment, onBack }: ShipmentDetailsProps) {
  const [items, setItems] = useState<ShipmentItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [itemValidationErrors, setItemValidationErrors] = useState<{[key: string]: string[]}>({})
  const [manufacturingAddresses, setManufacturingAddresses] = useState<ManufacturingAddress[]>([])
  const [showManualItemForm, setShowManualItemForm] = useState(false)
  const [manualItemsText, setManualItemsText] = useState('')
  const [addingManualItems, setAddingManualItems] = useState(false)
  const [manualItemError, setManualItemError] = useState<string | null>(null)
  
  // Commercial Invoice Generation State
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false)

  // CUSMA files refresh trigger
  const [cusmaFilesRefresh, setCusmaFilesRefresh] = useState(0)
  const [invoicePassword, setInvoicePassword] = useState('')
  const [ltlType, setLtlType] = useState<'direct_amazon' | 'pattern_middle_mile' | 'ups_buffalo'>('direct_amazon')
  const [brokerType, setBrokerType] = useState<'clearit' | 'acb' | 'jas'>('clearit')
  const [fcId, setFcId] = useState('')
  const [fcAddress, setFcAddress] = useState({
    street: '',
    city: '',
    state: '',
    zip: ''
  })
  const [generatingInvoice, setGeneratingInvoice] = useState(false)
  const [invoiceError, setInvoiceError] = useState<string | null>(null)
  const [exchangeRate, setExchangeRate] = useState<{cadToUsd: number, usdToCad: number} | null>(null)

  // SSB Submission State
  const [showSSBDialog, setShowSSBDialog] = useState(false)
  const [ssbPassword, setSSBPassword] = useState('')
  const [submittingSSB, setSubmittingSSB] = useState(false)
  const [ssbProgress, setSSBProgress] = useState('')
  const [ssbError, setSSBError] = useState<string | null>(null)
  const [catalogueFlagIssues, setCatalogueFlagIssues] = useState<CatalogueFlagIssue[]>([])
  const [ssbHTSStatuses, setSSBHTSStatuses] = useState<SSBHTSStatus[]>([])
  const [skipTracking, setSkipTracking] = useState(false)

  // SSBC Generation State
  const [showSSBCDialog, setShowSSBCDialog] = useState(false)
  const [ssbcPassword, setSsbcPassword] = useState('')
  const [generatingSSBC, setGeneratingSSBC] = useState(false)
  const [ssbcError, setSsbcError] = useState<string | null>(null)

  // Catalogue Upload State
  const [showCatalogueDialog, setShowCatalogueDialog] = useState(false)
  const [cataloguePassword, setCataloguePassword] = useState('')
  const [uploadingCatalogue, setUploadingCatalogue] = useState(false)
  const [catalogueError, setCatalogueError] = useState<string | null>(null)
  const [catalogueResult, setCatalogueResult] = useState<{
    uploaded: number
    updated: number
    skipped: number
    failed: number
    total: number
    results: Array<{ asin: string; status: string; reason?: string; error?: string }>
  } | null>(null)

  // Password cache state
  const [cachedPasswordMinutes, setCachedPasswordMinutes] = useState(0)
  const [trustedActionDevice, setTrustedActionDevice] = useState(false)

  // Service Breakdown State
  const [showServiceBreakdown, setShowServiceBreakdown] = useState(false)
  const [serviceBreakdownData, setServiceBreakdownData] = useState<any>(null)
  const [loadingServiceBreakdown, setLoadingServiceBreakdown] = useState(false)
  const [addingServices, setAddingServices] = useState(false)

  // CUSMA Certificate of Origin State - HIDDEN (form filling no longer needed, checkbox still works)
  // const [cusmaEligibility, setCusmaEligibility] = useState<any>(null)
  // const [cusmaForm, setCusmaForm] = useState<any>(null)
  // const [cusmaDataLoaded, setCusmaDataLoaded] = useState(false)
  // const [showCusmaModal, setShowCusmaModal] = useState(false)
  // const [cusmaLoading, setCusmaLoading] = useState(false)

  // Bulk Manufacturing Lookup State
  const [bulkMfgLookupProgress, setBulkMfgLookupProgress] = useState<{
    running: boolean
    currentItem: number
    totalItems: number
    currentItemName: string
    completed: number
    failed: number
    failedItems: string[] // Track ASINs/names of failed items
  } | null>(null)

  // Bulk HTS Classification State
  const [bulkHTSProgress, setBulkHTSProgress] = useState<{
    running: boolean
    currentItem: number
    totalItems: number
    currentItemName: string
    completed: number
    failed: number
    skipped: number // Items where no applicable code met criteria
    failedItems: string[]
    skippedItems: string[] // Items skipped due to no valid codes meeting criteria
  } | null>(null)

  // Bulk FDA Classification State
  const [bulkFDAProgress, setBulkFDAProgress] = useState<{
    running: boolean
    currentItem: number
    totalItems: number
    currentItemName: string
    foodCount: number
    cosmeticCount: number
    neitherCount: number
    failed: number
    failedItems: string[]
  } | null>(null)

  // Bulk SSB Rate Check State
  const [bulkSSBProgress, setBulkSSBProgress] = useState<{
    running: boolean
    currentItem: number
    totalItems: number
    currentItemName: string
    completed: number
    failed: number
    failedItems: string[]
  } | null>(null)

  const [bulkCOOProgress, setBulkCOOProgress] = useState<{
    running: boolean
    currentItem: number
    totalItems: number
    currentItemName: string
    filled: number
    manualReview: number
    matched: number
    noSuggestion: number
    failed: number
    reviewItems: Array<{ itemId: string; label: string; current: string; recommended: string }>
    noSuggestionItems: string[]
    failedItems: string[]
  } | null>(null)

  // SSB verified data map: item.id -> SSB result (passed down to child rows)
  const [bulkSsbResults, setBulkSsbResults] = useState<Record<string, {
    dutyAmount: number
    dutyAmountCAD?: number
    dutyAmountUSD?: number
    dutyRate: string
    dutyRatePercent?: number
    verified?: boolean
    cached?: boolean
    source?: string
    currency?: string
    htsCode?: string
    countryOfOrigin?: string
    timestamp: string
    cachedAt?: string | null
    cacheExpiresAt?: string
  }>>({})
  const ssbAutoLoadKeyRef = useRef('')
  const [ssbRatesLoading, setSsbRatesLoading] = useState(false)
  const [ssbRatesError, setSsbRatesError] = useState<string | null>(null)

  // UNIFIED DATA LOADING: Replace multiple API calls with single batched request
  useEffect(() => {
    loadCompleteShipmentData()
  }, [shipment.id])

  // Initialize passwords from cache on mount
  useEffect(() => {
    const cached = passwordCache.get()
    if (cached) {
      setInvoicePassword(cached)
      setSSBPassword(cached)
      setSsbcPassword(cached)
      setCataloguePassword(cached)
      setCachedPasswordMinutes(passwordCache.getRemainingMinutes())
    }
  }, [])

  useEffect(() => {
    const loadTrustedActionDeviceStatus = async () => {
      try {
        const response = await apiService.getTrustedActionDeviceStatus()
        setTrustedActionDevice(Boolean(response.success && response.data?.trusted))
      } catch (error) {
        console.warn('Could not load trusted device status:', error)
        setTrustedActionDevice(false)
      }
    }

    loadTrustedActionDeviceStatus()
  }, [])

  const loadCompleteShipmentData = async () => {
    try {
      setError(null)
      setLoading(true)

      const response = await apiService.getShipmentCompleteData(shipment.id)

      if (response.success && response.data) {
        const data = response.data

        // Set all data from single response
        setItems(data.items || [])
        setManufacturingAddresses(data.manufacturingAddresses || [])
        setExchangeRate(data.exchangeRate || { cadToUsd: 0.74, usdToCad: 1.35 })

        // CUSMA form data loading removed - form filling no longer needed
        // CUSMA checkbox in ShipmentItemRow still works for duty calculations
      } else {
        setError(response.error || 'Failed to load shipment data')
      }
    } catch (error: any) {
      console.error('Failed to load complete shipment data:', error)
      setError('Failed to connect to server. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const slugifyManualSku = (description: string) => (
    description
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 60) || `MANUAL-${Date.now()}`
  )

  const cleanManualItemTitle = (description: string) => (
    description.split(' - ')[0].trim().replace(/\s+/g, ' ')
  )

  const parseManualItemsText = (text: string) => {
    return text
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean)
      .map((line, index) => {
        const unitsMatch = line.match(/(.+?)\s+(\d+)\s+UNITS?\s*$/i)
        const pipeParts = line.split('|').map(part => part.trim()).filter(Boolean)

        if (pipeParts.length >= 2) {
          const quantity = Number.parseInt(pipeParts[pipeParts.length - 1], 10)
          const rawDescription = pipeParts.slice(0, -1).join(' | ')
          const description = cleanManualItemTitle(rawDescription)
          if (description && Number.isFinite(quantity) && quantity > 0) {
            return {
              sku: slugifyManualSku(rawDescription),
              description,
              quantity,
              currency: 'CAD' as const
            }
          }
        }

        if (unitsMatch) {
          const rawDescription = unitsMatch[1].trim().replace(/\s+/g, ' ')
          const description = cleanManualItemTitle(rawDescription)
          const quantity = Number.parseInt(unitsMatch[2], 10)
          return {
            sku: slugifyManualSku(rawDescription),
            description,
            quantity,
            currency: 'CAD' as const
          }
        }

        throw new Error(`Line ${index + 1} must end with a quantity, like "Product name 288 UNITS" or "Product name | 288"`)
      })
  }

  const handleAddManualItems = async () => {
    try {
      setAddingManualItems(true)
      setManualItemError(null)

      const parsedItems = parseManualItemsText(manualItemsText)
      if (parsedItems.length === 0) {
        setManualItemError('Add at least one manual item line.')
        return
      }

      const response = await apiService.addManualShipmentItems(shipment.id, parsedItems)
      if (response.success) {
        setItems(prev => [...prev, ...(response.data || [])])
        setManualItemsText('')
        setShowManualItemForm(false)
      } else {
        setManualItemError(response.error || 'Failed to add manual items')
      }
    } catch (error: any) {
      setManualItemError(error.message || 'Failed to add manual items')
    } finally {
      setAddingManualItems(false)
    }
  }

  // Legacy function kept for retry button - now delegates to unified loading
  const loadShipmentItems = async () => {
    await loadCompleteShipmentData()
  }

  const calculateTotals = () => {
    if (!items.length || !exchangeRate) {
      return null
    }

    // Check if all items have declared values
    const allItemsHaveValues = items.every(item =>
      item.declaredValue && item.declaredValue > 0
    )

    if (!allItemsHaveValues) {
      return null
    }

    // Calculate totals in CAD
    const totalCAD = items.reduce((sum, item) => {
      const itemValue = (item.declaredValue || 0) * item.quantity
      return sum + itemValue
    }, 0)

    const totalUSD = totalCAD * (exchangeRate?.cadToUsd || 0.74)

    const ssbEligibleItemCount = getItemsEligibleForSSBRateCheck().length
    const ssbRatedItemCount = items.filter(item => bulkSsbResults[item.id]?.verified).length

    // Calculate total duties across all items using SSB-verified rates only.
    const totalDutiesCAD = items.reduce((sum, item) => {
      const ssbResult = bulkSsbResults[item.id]
      const dutyPerUnit = ssbResult?.verified ? Number(ssbResult.dutyAmount) || 0 : 0
      return sum + (dutyPerUnit * item.quantity)
    }, 0)

    return { totalCAD, totalUSD, totalDutiesCAD, exchangeRate, ssbEligibleItemCount, ssbRatedItemCount }
  }

  const handleNewAddressSaved = async (newAddress: ManufacturingAddress & { id: number }) => {
    // Add the new address to the list immediately
    setManufacturingAddresses(prev => [...prev, newAddress])
  }

  // Get items that need manufacturing lookup:
  // 1. No existing address AND has country of origin, OR
  // 2. Has address but country doesn't match country of origin (mismatch)
  const getItemsNeedingMfgLookup = () => {
    return items.filter(item => {
      if (!item.countryOfOrigin || !item.description) return false // Must have COO and description

      // No manufacturing address - needs lookup
      if (!item.manufacturingAddress) return true

      // Has address but country doesn't match COO - needs new lookup
      const mfgCountry = item.manufacturingAddress.country?.toUpperCase()
      const cooCountry = item.countryOfOrigin?.toUpperCase()
      if (mfgCountry && cooCountry && mfgCountry !== cooCountry) {
        return true // Country mismatch - needs re-lookup
      }

      return false
    })
  }

  // Get items missing country of origin (will be skipped)
  const getItemsMissingCOO = () => {
    return items.filter(item =>
      !item.manufacturingAddress && // No existing manufacturing address
      !item.countryOfOrigin && // Missing country of origin
      item.description // Has a description
    )
  }

  const getItemsEligibleForYYZCOO = () => {
    return items.filter(item => item.asin || item.sku)
  }

  const normalizeCOO = (country?: string | null) => (country || '').trim().toUpperCase()

  const getItemLabel = (item: ShipmentItem) => (
    item.asin || item.sku || item.description?.substring(0, 50) || item.id
  )

  const handleApplyYYZRecommendedCOO = async () => {
    const itemsToProcess = getItemsEligibleForYYZCOO()

    if (itemsToProcess.length === 0) {
      alert('No items have an ASIN or SKU to check against YYZ country recommendations.')
      return
    }

    const progress = {
      running: true,
      currentItem: 0,
      totalItems: itemsToProcess.length,
      currentItemName: '',
      filled: 0,
      manualReview: 0,
      matched: 0,
      noSuggestion: 0,
      failed: 0,
      reviewItems: [] as Array<{ itemId: string; label: string; current: string; recommended: string }>,
      noSuggestionItems: [] as string[],
      failedItems: [] as string[]
    }
    setBulkCOOProgress(progress)

    for (let i = 0; i < itemsToProcess.length; i++) {
      const item = itemsToProcess[i]
      const productKey = item.asin || item.sku || ''
      const label = getItemLabel(item)

      setBulkCOOProgress(prev => prev ? {
        ...prev,
        currentItem: i + 1,
        currentItemName: label
      } : prev)

      try {
        const response = await apiService.getCountrySuggestion(productKey)
        const recommended = response.success && response.data?.countryCode
          ? normalizeCOO(response.data.countryCode)
          : ''

        if (!recommended) {
          progress.noSuggestion += 1
          progress.noSuggestionItems.push(label)
          setItems(prevItems => prevItems.map(prevItem =>
            prevItem.id === item.id
              ? {
                  ...prevItem,
                  yyzRecommendedCountryOfOrigin: null,
                  cooReviewStatus: null,
                  cooReviewMessage: 'No YYZ country recommendation found'
                }
              : prevItem
          ))
          setBulkCOOProgress(prev => prev ? { ...prev, ...progress, currentItem: i + 1, currentItemName: label } : prev)
          continue
        }

        const current = normalizeCOO(item.countryOfOrigin)

        if (!current) {
          await apiService.updateShipmentItem(shipment.id, item.id, { countryOfOrigin: recommended })
          await apiService.saveClientASINData(productKey, {
            country_of_origin: recommended,
            outbound_id: shipment.id,
            change_source: 'bulk_country_fill'
          })

          progress.filled += 1
          setItems(prevItems => prevItems.map(prevItem =>
            prevItem.id === item.id
              ? {
                  ...prevItem,
                  countryOfOrigin: recommended,
                  yyzRecommendedCountryOfOrigin: recommended,
                  cooReviewStatus: 'auto_filled',
                  cooReviewMessage: 'Filled from YYZ recommendation'
                }
              : prevItem
          ))
        } else if (current !== recommended) {
          progress.manualReview += 1
          progress.reviewItems.push({
            itemId: item.id,
            label,
            current,
            recommended
          })
          setItems(prevItems => prevItems.map(prevItem =>
            prevItem.id === item.id
              ? {
                  ...prevItem,
                  yyzRecommendedCountryOfOrigin: recommended,
                  cooReviewStatus: 'manual_review',
                  cooReviewMessage: `Manual review: current ${current} does not match YYZ ${recommended}`
                }
              : prevItem
          ))
        } else {
          progress.matched += 1
          setItems(prevItems => prevItems.map(prevItem =>
            prevItem.id === item.id
              ? {
                  ...prevItem,
                  yyzRecommendedCountryOfOrigin: recommended,
                  cooReviewStatus: 'matched',
                  cooReviewMessage: null
                }
              : prevItem
          ))
        }
      } catch (error: any) {
        progress.failed += 1
        progress.failedItems.push(`${label}: ${error.message || 'Failed to check YYZ country recommendation'}`)
      }

      setBulkCOOProgress(prev => prev ? { ...prev, ...progress, currentItem: i + 1, currentItemName: label } : prev)
    }

    setBulkCOOProgress(prev => prev ? { ...prev, running: false, currentItemName: 'Complete' } : prev)
  }

  // Get items that need HTS classification (no HTS code AND has description)
  const getItemsNeedingHTSClassification = () => {
    return items.filter(item => {
      const htsDigits = (item.htsCode || '').replace(/\D/g, '')
      return (
        htsDigits.length !== 10 &&
        item.description
      )
    })
  }

  // Get items that need FDA classification (not yet classified as food or cosmetic)
  const getItemsNeedingFDAClassification = () => {
    return items.filter(item =>
      !(item as any).fdaClassification && // Not already checked as FOOD/COSMETIC/NEITHER
      !item.is_food && // Not marked as food
      !item.is_cosmetic_product && // Not marked as cosmetic
      item.description // Has a description to classify
    )
  }

  // Get items eligible for SSB rate check (10-digit HTS, country of origin, COGS > 0)
  const getItemsEligibleForSSBRateCheck = () => {
    return items.filter(item => {
      const htsDigits = (item.htsCode || '').replace(/\D/g, '')
      return (
        htsDigits.length === 10 &&
        item.countryOfOrigin &&
        item.declaredValue &&
        item.declaredValue > 0
      )
    })
  }

  const buildSSBRateItemPayload = (item: ShipmentItem) => ({
    itemId: item.id,
    htsCode: item.htsCode!,
    countryOfOrigin: item.countryOfOrigin!,
    price: item.declaredValue!,
    description: item.description,
    asin: item.asin,
    manufacturingAddress: item.manufacturingAddress?.street_address,
    metal_aluminum: item.metal_aluminum ?? item.metalAluminum ?? null,
    metal_copper: item.metal_copper ?? item.metalCopper ?? null,
    metal_steel: item.metal_steel ?? item.metalSteel ?? null
  })

  const buildSSBRateLoadKey = (itemsToProcess: ShipmentItem[]) => (
    itemsToProcess
      .map(item => [
        item.id,
        (item.htsCode || '').replace(/\D/g, ''),
        item.countryOfOrigin || '',
        item.declaredValue || 0,
        item.metal_aluminum ?? item.metalAluminum ?? '',
        item.metal_copper ?? item.metalCopper ?? '',
        item.metal_steel ?? item.metalSteel ?? ''
      ].join('|'))
      .sort()
      .join(';')
  )

  const loadSSBRatesForItems = async (
    itemsToProcess: ShipmentItem[],
    options: { showAlert?: boolean; automatic?: boolean } = {}
  ) => {
    if (itemsToProcess.length === 0) return

    const showAlert = Boolean(options.showAlert)
    const automatic = Boolean(options.automatic)

    if (automatic) {
      setSsbRatesLoading(true)
      setSsbRatesError(null)
    } else {
      setSsbRatesError(null)
      setBulkSSBProgress({
        running: true,
        currentItem: 0,
        totalItems: itemsToProcess.length,
        currentItemName: 'Checking with SSB',
        completed: 0,
        failed: 0,
        failedItems: []
      })
    }

    try {
      const response = await apiService.getSSBRatesForItems(itemsToProcess.map(buildSSBRateItemPayload))
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load SSB rates')
      }

      const failedItems: string[] = []
      const verifiedResults: typeof bulkSsbResults = {}

      response.data.results.forEach(result => {
        if (result.success && result.itemId && result.dutyAmount !== undefined && result.dutyRate && result.timestamp) {
          verifiedResults[result.itemId] = {
            dutyAmount: Number(result.dutyAmount) || 0,
            dutyAmountCAD: result.dutyAmountCAD,
            dutyAmountUSD: result.dutyAmountUSD,
            dutyRate: result.dutyRate,
            dutyRatePercent: result.dutyRatePercent,
            verified: Boolean(result.verified),
            cached: Boolean(result.cached),
            source: result.source,
            currency: result.currency,
            htsCode: result.htsCode,
            countryOfOrigin: result.countryOfOrigin,
            timestamp: result.timestamp,
            cachedAt: result.cachedAt,
            cacheExpiresAt: result.cacheExpiresAt
          }
        } else {
          const item = itemsToProcess.find(candidate => candidate.id === result.itemId)
          const itemName = item?.asin || item?.description?.substring(0, 25) || result.itemId || 'Unknown item'
          failedItems.push(`${itemName}: ${result.error || 'Unknown error'}`)
        }
      })

      setBulkSsbResults(prev => ({
        ...prev,
        ...verifiedResults
      }))

      if (!automatic) {
        setBulkSSBProgress({
          running: true,
          currentItem: itemsToProcess.length,
          totalItems: itemsToProcess.length,
          currentItemName: 'Done',
          completed: response.data.summary.verified,
          failed: response.data.summary.failed,
          failedItems
        })
      }

      if (failedItems.length > 0) {
        setSsbRatesError(`${failedItems.length} SSB rate check(s) failed:\n${failedItems.join('\n')}`)
      } else {
        setSsbRatesError(null)
      }

      if (showAlert) {
        const messages = []
        messages.push(`Verified: ${response.data.summary.verified} item(s)`)
        if (response.data.summary.cusma > 0) messages.push(`CUSMA duty-free: ${response.data.summary.cusma}`)
        if (response.data.summary.cached > 0) messages.push(`Cached: ${response.data.summary.cached}`)
        if (response.data.summary.fetched > 0) messages.push(`Fetched from SSB: ${response.data.summary.fetched}`)
        if (response.data.summary.failed > 0) messages.push(`Failed: ${response.data.summary.failed}`)
        alert(`SSB Rate Check Complete\n\n${messages.join('\n')}`)
      }
    } catch (error: any) {
      if (automatic) {
        setSsbRatesError(error.message || 'Failed to load SSB rates')
      } else if (showAlert) {
        alert('Failed to check SSB rates: ' + (error.message || 'Unknown error'))
      }
    } finally {
      if (automatic) {
        setSsbRatesLoading(false)
      } else {
        setTimeout(() => setBulkSSBProgress(null), 400)
      }
    }
  }

  useEffect(() => {
    const itemsToProcess = getItemsEligibleForSSBRateCheck()
    const loadKey = buildSSBRateLoadKey(itemsToProcess)

    if (!loadKey) {
      ssbAutoLoadKeyRef.current = ''
      setBulkSsbResults({})
      return
    }

    if (loadKey === ssbAutoLoadKeyRef.current) return
    ssbAutoLoadKeyRef.current = loadKey
    void loadSSBRatesForItems(itemsToProcess, { automatic: true })
  }, [items])

  const getHTSOptionCode = (code: any): string => {
    return code?.formattedHtsCode || code?.htsCode || code?.TariffNumFormatted || code?.TariffNum || ''
  }

  const getHTSOptionConfidence = (code: any): number => {
    return Number(code?.confidence ?? code?.accuracyMatch ?? code?.score ?? 0) || 0
  }

  const isSelectableHTSOption = (code: any): boolean => {
    const htsDigits = getHTSOptionCode(code).replace(/\D/g, '')
    const applicability = String(code?.applicability || '').toLowerCase()
    return (
      htsDigits.length === 10 &&
      code?.isValid !== false &&
      applicability !== 'not_applicable'
    )
  }

  // Find the best applicable HTS code by validation and confidence only.
  const findBestHTSCode = (htsCodes: any[], minConfidence: number = 80): any | null => {
    if (!htsCodes || htsCodes.length === 0) return null

    const selectableCodes = htsCodes.filter(isSelectableHTSOption)
    if (selectableCodes.length === 0) return null

    const sortByConfidence = (codes: any[]) => [...codes].sort((a, b) => getHTSOptionConfidence(b) - getHTSOptionConfidence(a))

    const preferredCodes = selectableCodes.filter(code => getHTSOptionConfidence(code) >= minConfidence)
    if (preferredCodes.length > 0) {
      return sortByConfidence(preferredCodes)[0]
    }

    const fallbackCodes = selectableCodes.filter(code => getHTSOptionConfidence(code) >= 70)
    if (fallbackCodes.length > 0) {
      return sortByConfidence(fallbackCodes)[0]
    }

    return null
  }

  const handleBulkMfgLookup = async () => {
    const itemsToProcess = getItemsNeedingMfgLookup()

    if (itemsToProcess.length === 0) {
      alert('No items need manufacturing lookup. All items either already have a manufacturing address or are missing a country of origin.')
      return
    }

    const BATCH_SIZE = 10 // Process up to 10 items in parallel

    // Initialize progress state
    setBulkMfgLookupProgress({
      running: true,
      currentItem: 0,
      totalItems: itemsToProcess.length,
      currentItemName: '',
      completed: 0,
      failed: 0,
      failedItems: []
    })

    let completed = 0
    let failed = 0
    const failedItems: string[] = []

    // Process a single item and return result
    const processItem = async (item: ShipmentItem): Promise<{ success: boolean; item: ShipmentItem; savedAddressId?: number }> => {
      try {
        // Call AI manufacturing lookup
        const response = await apiService.lookupManufacturingAddress(
          item.description,
          item.asin || undefined,
          item.countryOfOrigin || undefined
        )

        if (response.success && response.data && response.data.name) {
          const addressData = response.data
          const newAddress: ManufacturingAddress = {
            name: addressData.name,
            street_address: addressData.street_address || '',
            city: addressData.city || '',
            state: addressData.state || '',
            country: addressData.country || item.countryOfOrigin || '',
            zip_code: addressData.zip_code || '',
            reasoning: addressData.reasoning || ''
          }

          // Save the address to the database
          try {
            const saveResponse = await apiService.saveClientManufacturingAddress(newAddress)

            if (saveResponse.success && saveResponse.data) {
              const savedAddress = { ...newAddress, id: saveResponse.data.id }

              // Update the item with the new address (savedAddress includes .id which triggers useEffect in ShipmentItemRow)
              setItems(prevItems =>
                prevItems.map(prevItem =>
                  prevItem.id === item.id
                    ? { ...prevItem, manufacturingAddress: savedAddress }
                    : prevItem
                )
              )

              // Add to manufacturing addresses list
              setManufacturingAddresses(prev => {
                // Check if address already exists
                if (prev.some(addr => addr.id === savedAddress.id)) {
                  return prev
                }
                return [...prev, savedAddress]
              })

              // Save to ASIN data
              if (item.asin) {
                await apiService.saveClientASINData(item.asin, {
                  manufacturing_address: savedAddress,
                  manufacturing_address_id: savedAddress.id,
                  outbound_id: shipment.id,
                  change_source: 'bulk_manufacturer_lookup'
                })
              }

              return { success: true, item, savedAddressId: savedAddress.id }
            } else {
              console.error(`Failed to save address for ${item.description}:`, saveResponse.error)
              return { success: false, item }
            }
          } catch (saveError) {
            console.error(`Error saving address for ${item.description}:`, saveError)
            return { success: false, item }
          }
        } else {
          console.log(`No address found for ${item.description}`)
          return { success: false, item }
        }
      } catch (itemError: any) {
        console.error(`Error looking up ${item.description}:`, itemError)
        return { success: false, item }
      }
    }

    // Process items in batches of BATCH_SIZE
    for (let batchStart = 0; batchStart < itemsToProcess.length; batchStart += BATCH_SIZE) {
      const batchEnd = Math.min(batchStart + BATCH_SIZE, itemsToProcess.length)
      const batch = itemsToProcess.slice(batchStart, batchEnd)

      // Update progress to show current batch
      const batchNames = batch.map(item =>
        item.asin || item.description?.substring(0, 20) || 'Unknown'
      ).join(', ')

      setBulkMfgLookupProgress(prev => prev ? {
        ...prev,
        currentItem: batchEnd,
        currentItemName: `Batch ${Math.floor(batchStart / BATCH_SIZE) + 1}: ${batchNames}`
      } : null)

      // Process all items in this batch in parallel
      const results = await Promise.all(batch.map(item => processItem(item)))

      // Count results and track failed items
      for (const result of results) {
        if (result.success) {
          completed++
        } else {
          failed++
          // Track the failed item's ASIN or description
          const failedName = result.item.asin || result.item.description?.substring(0, 30) || 'Unknown'
          failedItems.push(failedName)
        }
      }

      // Update progress counts with failed items list
      setBulkMfgLookupProgress(prev => prev ? {
        ...prev,
        completed,
        failed,
        failedItems: [...failedItems]
      } : null)
    }

    // Clear progress after completion
    setBulkMfgLookupProgress(null)

    // Show completion message
    if (completed > 0) {
      alert(`Manufacturing lookup completed!\n\n${completed} addresses found and saved\n${failed} items could not be found`)
    } else {
      alert(`Manufacturing lookup completed, but no addresses could be found for any items. The AI may need more specific product information.`)
    }
  }

  // Legacy function - kept for compatibility but now delegates to bulk lookup
  const handleRunAILookup = async () => {
    await handleBulkMfgLookup()
  }

  // Bulk HTS Classification with auto-selection of the highest-confidence applicable code
  const handleBulkHTSClassification = async () => {
    const itemsToProcess = getItemsNeedingHTSClassification()

    if (itemsToProcess.length === 0) {
      alert('No items need HTS classification. All items either already have an HTS code or are missing a country of origin.')
      return
    }

    // Initialize progress state
    setBulkHTSProgress({
      running: true,
      currentItem: 0,
      totalItems: itemsToProcess.length,
      currentItemName: '',
      completed: 0,
      failed: 0,
      skipped: 0,
      failedItems: [],
      skippedItems: []
    })

    let completed = 0
    let failed = 0
    let skipped = 0
    const failedItems: string[] = []
    const skippedItems: string[] = []

    try {
      setBulkHTSProgress(prev => prev ? {
        ...prev,
        currentItem: 0,
        currentItemName: `Classifying ${itemsToProcess.length} item(s) on the backend...`
      } : null)

      const response = await apiService.classifyHTSBatch(itemsToProcess.map(item => ({
        id: item.id,
        sku: item.sku,
        description: item.description,
        countryOfOrigin: item.countryOfOrigin || undefined,
        asin: item.asin || undefined,
        imageUrl: item.imageUrl || undefined
      })), true)

      const results = Array.isArray((response as any).results) ? (response as any).results : []
      if (!response.success || results.length === 0) {
        throw new Error(response.error || 'No HTS classification results returned')
      }

      for (let index = 0; index < results.length; index++) {
        const result = results[index]
        const item = itemsToProcess.find(candidate => candidate.id === result.itemId) || itemsToProcess[index]
        const label = item?.asin || item?.description?.substring(0, 30) || result.sku || 'Unknown'

        setBulkHTSProgress(prev => prev ? {
          ...prev,
          currentItem: index + 1,
          currentItemName: label
        } : null)

        const responseHTSCodes = result.htsCodes?.length
          ? result.htsCodes
          : result.htsCode
            ? [{
                htsCode: result.htsCode,
                description: result.description,
                confidence: result.confidence,
                reasoning: result.reasoning,
                isValid: result.isValid,
                htsValidation: result.htsValidation
              }]
            : []

        if (result.success && responseHTSCodes.length > 0 && item) {
          const bestCode = findBestHTSCode(responseHTSCodes, 80)

          if (bestCode) {
            const htsCodeValue = getHTSOptionCode(bestCode)

            setItems(prevItems =>
              prevItems.map(prevItem =>
                prevItem.id === item.id
                  ? { ...prevItem, htsCode: htsCodeValue }
                  : prevItem
              )
            )

            if (item.asin) {
              try {
                await apiService.saveClientASINData(item.asin, {
                  hts_code: htsCodeValue,
                  hts_description: bestCode.description || bestCode.fullTitle || bestCode.officialDescription || '',
                  hts_confidence: getHTSOptionConfidence(bestCode),
                  outbound_id: shipment.id,
                  change_source: 'bulk_classification'
                })
              } catch (saveError) {
                console.warn(`HTS ${htsCodeValue} selected for ${item.asin}, but saved ASIN memory update failed:`, saveError)
              }
            }

            completed++
          } else {
            skipped++
            skippedItems.push(label)
          }
        } else {
          failed++
          failedItems.push(`${label}${result.error ? `: ${result.error}` : ''}`)
        }

        setBulkHTSProgress(prev => prev ? {
          ...prev,
          completed,
          failed,
          skipped,
          failedItems: [...failedItems],
          skippedItems: [...skippedItems]
        } : null)
      }
    } catch (error: any) {
      failed = itemsToProcess.length
      failedItems.push(error.message || 'Backend HTS batch classification failed')
      setBulkHTSProgress(prev => prev ? {
        ...prev,
        currentItem: itemsToProcess.length,
        completed,
        failed,
        skipped,
        failedItems: [...failedItems],
        skippedItems: [...skippedItems]
      } : null)
    }

    // Clear progress after completion
    setBulkHTSProgress(null)

    // Show completion message
    const messages = []
    if (completed > 0) messages.push(`${completed} HTS codes auto-selected (highest-confidence applicable option)`)
    if (skipped > 0) messages.push(`⚠️ ${skipped} items skipped (no valid applicable code with at least 70% confidence)`)
    if (failed > 0) messages.push(`❌ ${failed} items failed classification`)

    if (messages.length > 0) {
      alert(`HTS Auto-Classification Complete\n\n${messages.join('\n')}`)
    } else {
      alert('HTS classification completed but no items were processed.')
    }
  }

  // Bulk FDA Classification - determines if items are food, cosmetic, or neither
  const handleBulkFDAClassification = async () => {
    const itemsToProcess = getItemsNeedingFDAClassification()

    if (itemsToProcess.length === 0) {
      alert('No items need FDA classification. All items are already classified as food or cosmetic.')
      return
    }

    const BATCH_SIZE = 1 // FDA classification can trigger multiple provider calls per item

    // Initialize progress state
    setBulkFDAProgress({
      running: true,
      currentItem: 0,
      totalItems: itemsToProcess.length,
      currentItemName: '',
      foodCount: 0,
      cosmeticCount: 0,
      neitherCount: 0,
      failed: 0,
      failedItems: []
    })

    let foodCount = 0
    let cosmeticCount = 0
    let neitherCount = 0
    let failed = 0
    const failedItems: string[] = []

    // Process a single item
    const processItem = async (item: ShipmentItem) => {
      try {
        const fdaProductTitle = item.customsDescription?.trim() || item.description || ''
        const response = await apiService.autoClassifyFDA(
          fdaProductTitle,
          item.htsCode,
          item.htsDescription
        ) as any

        console.log(`📋 FDA API response for ${item.asin}:`, JSON.stringify(response))

        if (response.success) {
          // Access properties directly from response (backend returns flat structure)
          const isFood = response.isFood === true
          const isCosmetic = response.isCosmetic === true
          const classification = response.classification || (isFood ? 'FOOD' : isCosmetic ? 'COSMETIC' : 'NEITHER')
          const fdaCode = response.fdaCode || null

          console.log(`📋 ${item.asin}: classification=${classification}, is_food=${isFood}, is_cosmetic=${isCosmetic}, fda_code=${fdaCode}`)

          // Build the data to save
          const dataToSave: any = {
            is_food: isFood,
            is_cosmetic_product: isCosmetic,
            fda_classification: classification,
            outbound_id: shipment.id,
            change_source: 'bulk_fda_classification'
          }

          // If it's a food product and we got an FDA code, include it
          if (isFood && fdaCode) {
            dataToSave.fda_product_code = fdaCode
          } else if (!isFood) {
            dataToSave.fda_product_code = null
          }

          // Only save if we have a valid ASIN
          if (item.asin) {
            try {
              console.log(`💾 Saving FDA data for ${item.asin}:`, JSON.stringify(dataToSave))
              // Note: ShipmentDetails is for regular client users, clientId comes from JWT token
              const saveResponse = await apiService.saveClientASINData(
                item.asin,
                dataToSave,
                undefined  // Regular users don't need to pass clientId
              )
              console.log(`✅ Save response for ${item.asin}:`, JSON.stringify(saveResponse))
            } catch (saveError: any) {
              console.error(`❌ Failed to save FDA data for ${item.asin}:`, saveError.message)
              // Continue anyway - classification succeeded even if save failed
            }
          } else {
            console.log(`⚠️ No ASIN - skipping database save`)
          }

          // Update local item state
          const updatedItem = {
            ...item,
            is_food: isFood,
            is_cosmetic_product: isCosmetic,
            fdaProductCode: isFood && fdaCode ? fdaCode : null,
            fdaClassification: classification
          }

          // Update items array
          setItems(prevItems =>
            prevItems.map(i => i.id === item.id ? updatedItem : i)
          )

          if (isFood) {
            console.log(`🍎 ${item.asin || item.description?.substring(0, 20)}: FOOD${fdaCode ? ` (FDA: ${fdaCode})` : ''}`)
            return { success: true, type: 'food', item }
          } else if (isCosmetic) {
            console.log(`💄 ${item.asin || item.description?.substring(0, 20)}: COSMETIC`)
            return { success: true, type: 'cosmetic', item }
          } else {
            console.log(`⏭️ ${item.asin || item.description?.substring(0, 20)}: NEITHER`)
            return { success: true, type: 'neither', item }
          }
        } else {
          console.log(`❌ FDA classification failed for ${item.asin || item.description?.substring(0, 20)}: ${response.error || 'Unknown error'}`)
          return { success: false, item }
        }
      } catch (itemError: any) {
        console.error(`❌ Error classifying FDA for ${item.asin || item.description?.substring(0, 20)}:`, itemError.message || itemError)
        return { success: false, item }
      }
    }

    // Process items in batches
    for (let batchStart = 0; batchStart < itemsToProcess.length; batchStart += BATCH_SIZE) {
      const batchEnd = Math.min(batchStart + BATCH_SIZE, itemsToProcess.length)
      const batch = itemsToProcess.slice(batchStart, batchEnd)

      // Update progress to show current batch
      const batchNames = batch.map(item =>
        item.asin || item.description?.substring(0, 20) || 'Unknown'
      ).join(', ')

      setBulkFDAProgress(prev => prev ? {
        ...prev,
        currentItem: batchEnd,
        currentItemName: `Batch ${Math.floor(batchStart / BATCH_SIZE) + 1}: ${batchNames}`
      } : null)

      // Process all items in this batch in parallel
      const results = await Promise.all(batch.map(item => processItem(item)))

      // Count results
      for (const result of results) {
        if (result.success) {
          if (result.type === 'food') foodCount++
          else if (result.type === 'cosmetic') cosmeticCount++
          else neitherCount++
        } else {
          failed++
          const failedName = result.item.asin || result.item.description?.substring(0, 30) || 'Unknown'
          failedItems.push(failedName)
        }
      }

      // Update progress counts
      setBulkFDAProgress(prev => prev ? {
        ...prev,
        foodCount,
        cosmeticCount,
        neitherCount,
        failed,
        failedItems: [...failedItems]
      } : null)
    }

    // Clear progress after completion
    setBulkFDAProgress(null)

    // Show completion message
    const messages = []
    if (foodCount > 0) messages.push(`🍎 ${foodCount} food product(s) classified`)
    if (cosmeticCount > 0) messages.push(`💄 ${cosmeticCount} cosmetic product(s) classified`)
    if (neitherCount > 0) messages.push(`⏭️ ${neitherCount} item(s) are neither food nor cosmetic`)
    if (failed > 0) messages.push(`❌ ${failed} item(s) failed classification`)

    if (messages.length > 0) {
      alert(`FDA Auto-Classification Complete\n\n${messages.join('\n')}`)
    } else {
      alert('FDA classification completed but no items were processed.')
    }
  }

  // Bulk SSB Rate Check - uses server-side SSB rate cache and fetches misses
  const handleBulkSSBRateCheck = async () => {
    const itemsToProcess = getItemsEligibleForSSBRateCheck()

    if (itemsToProcess.length === 0) {
      alert('No items are eligible for SSB rate check. Items need a 10-digit HTS code, Country of Origin, and COGS > 0.')
      return
    }

    await loadSSBRatesForItems(itemsToProcess, { showAlert: true })
  }

  const handleSubmitToSSB = async () => {
    try {
      setSubmittingSSB(true)
      setSSBProgress('Preparing shipment…')
      setSSBError(null)
      setCatalogueFlagIssues([])
      setSSBHTSStatuses([])

      if (!trustedActionDevice && !ssbPassword.trim()) {
        setSSBError('Password is required')
        setSubmittingSSB(false)
        return
      }

      if (ssbPassword.trim()) {
        passwordCache.set(ssbPassword)
      }

      // Debug: Log items before submission to check manufacturingAddress and customsDescription
      console.log('📦 SSB Submission - Items being sent:')
      console.log(`🏷️ Skip Tracking: ${skipTracking}`)
      items.forEach((item, idx) => {
        const coo = item.countryOfOrigin || ''
        const hasMfg = !!item.manufacturingAddress
        const hasCustomDesc = !!item.customsDescription
        console.log(`  Item ${idx + 1}: ASIN=${item.asin}, COO=${coo}, hasMfg=${hasMfg}, hasCustomDesc=${hasCustomDesc}`)
        if (hasCustomDesc) {
          console.log(`    📝 customsDescription: "${item.customsDescription?.substring(0, 50)}..."`)
        }
      })

      const response = await apiService.submitToSSB(
        shipment.id,
        ssbPassword,
        items,
        skipTracking,
        setSSBProgress
      )

      if (response.success) {
        // Close the dialog and reset form
        setShowSSBDialog(false)
        setSSBPassword('')
        setSkipTracking(false)

        alert(response.message || 'Shipment successfully submitted to SSB!')

        // Open SSB in a new tab
        window.open('https://my.shipsavvy.com/shipping', '_blank')
      } else {
        setSSBError(response.error || 'Failed to submit to SSB')
      }
    } catch (error: any) {
      console.error('SSB submission error:', error)
      const flagIssues = error?.responseData?.catalogueFlagIssues
      if (Array.isArray(flagIssues)) {
        setCatalogueFlagIssues(flagIssues)
      }
      const syncRows = Array.isArray(error?.responseData?.htsSyncReport) ? error.responseData.htsSyncReport : []
      const syncStatuses: SSBHTSStatus[] = syncRows.flatMap((row: any) =>
        (row.changes || []).map((change: any) => ({
          asin: change.asin,
          description: change.description,
          rejectedHts: change.oldHtsCode,
          acceptedHts: change.newHtsCode,
          status: row.checkoutAccepted ? 'accepted' : 'applied',
          shipments: [row.shipmentShortId || row.shipmentUuid].filter(Boolean),
          message: row.checkoutAccepted ? 'Applied to shipment and accepted by ShipSavvy' : 'Applied to shipment; checkout is still pending'
        }))
      )
      const diagnosticStatuses: SSBHTSStatus[] = (error?.responseData?.errors || []).flatMap((batch: any) =>
        (batch.dutyDiagnosticFailures || []).map((failure: any) => ({
          asin: failure.sku,
          description: failure.description,
          rejectedHts: failure.hts_code,
          status: 'rejected',
          shipments: [],
          message: failure.error || 'Rejected during ShipSavvy duty calculation'
        }))
      )
      setSSBHTSStatuses([...syncStatuses, ...diagnosticStatuses])
      setSSBError(error.message || 'Failed to submit to SSB')
    } finally {
      setSubmittingSSB(false)
    }
  }

  const openSSBDialog = async () => {
    setShowSSBDialog(true)
    setSSBHTSStatuses([])
    try {
      const response = await apiService.getSSBHTSAudit(shipment.id)
      const events = Array.isArray(response.data) ? response.data : []
      setSSBHTSStatuses(events.map((event: any) => ({
        asin: event.asin || event.product_key,
        description: event.metadata?.description,
        rejectedHts: event.old_hts_code,
        acceptedHts: event.new_hts_code && event.new_hts_code !== event.old_hts_code ? event.new_hts_code : undefined,
        status: event.metadata?.status === 'accepted' ? 'accepted' : event.metadata?.status === 'rejected' ? 'rejected' : 'applied',
        shipments: event.metadata?.affectedShipments || [event.metadata?.shipmentShortId].filter(Boolean),
        message: event.reason
      })))
    } catch (auditError) {
      console.warn('Could not load SSB HTS audit history', auditError)
    }
  }

  const handleGenerateSSBC = async () => {
    try {
      setGeneratingSSBC(true)
      setSsbcError(null)

      // Validate password
      if (!ssbcPassword) {
        setSsbcError('Password is required')
        setGeneratingSSBC(false)
        return
      }

      // Cache the password for 2 hours
      passwordCache.set(ssbcPassword)

      console.log('📋 Generating SSBC Excel for shipment:', shipment.id)

      // Backend automatically fetches tracking from YYZ Prep API (same as SSB submission)
      const blob = await apiService.generateSSBC(shipment.id, ssbcPassword)

      // Download the Excel file
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `SSBC_Shipment_${shipment.id}_${Date.now()}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      console.log('✅ SSBC Excel downloaded successfully')

      // Close dialog and reset password
      setShowSSBCDialog(false)
      setSsbcPassword('')
      alert('SSBC Excel file generated successfully!')

    } catch (error: any) {
      console.error('❌ SSBC generation error:', error)
      setSsbcError(error.message || 'Failed to generate SSBC')
    } finally {
      setGeneratingSSBC(false)
    }
  }

  const handleUploadToCatalogue = async () => {
    try {
      setUploadingCatalogue(true)
      setCatalogueError(null)
      setCatalogueResult(null)

      // Validate password unless this browser has been allowlisted on the backend.
      if (!trustedActionDevice && !cataloguePassword) {
        setCatalogueError('Password is required')
        setUploadingCatalogue(false)
        return
      }

      if (cataloguePassword.trim()) {
        passwordCache.set(cataloguePassword)
      }

      // Debug: Log items state to see FDA flags
      console.log('📋 Items state before catalogue upload:')
      items.forEach(item => {
        console.log(`   ${item.asin}: is_food=${item.is_food}, isFood=${(item as any).isFood}, is_cosmetic_product=${item.is_cosmetic_product}, fdaProductCode=${(item as any).fdaProductCode}`)
      })

      const normalizeOrigin = (value: any) => {
        const origin = String(value || '').trim().toUpperCase()
        if (['US', 'USA', 'UNITED STATES', 'UNITED STATES OF AMERICA'].includes(origin)) return 'US'
        if (['CA', 'CAN', 'CANADA'].includes(origin)) return 'CA'
        if (['MX', 'MEX', 'MEXICO'].includes(origin)) return 'MX'
        return origin
      }
      const isNorthAmericanOrigin = (item: any) =>
        ['US', 'CA', 'MX'].includes(normalizeOrigin(
          item.countryOfOrigin ||
          item.country_of_origin ||
          item.manufacturingAddress?.country ||
          item.manufacturing_address?.country
        ))

      // Log what the current UI already knows, but let the backend make the
      // final upload decision after it enriches items from saved ASIN data.
      const catalogueItems = items.filter(item =>
        item.is_food ||
        (item as any).isFood ||
        (item as any).is_cosmetic ||
        (item as any).isCosmetic ||
        item.is_cosmetic_product ||
        (item as any).fda_product_code ||
        (item as any).fdaProductCode ||
        (item as any).cusmaApplicable ||
        (item as any).is_usmca_certified ||
        (item as any).isUSMCA ||
        isNorthAmericanOrigin(item)
      )

      const foodCount = catalogueItems.filter(i => i.is_food || (i as any).isFood).length
      const cosmeticCount = catalogueItems.filter(i => (i as any).is_cosmetic || (i as any).isCosmetic || i.is_cosmetic_product).length
      const northAmericanCount = catalogueItems.filter(isNorthAmericanOrigin).length
      console.log(`📦 Frontend sees ${catalogueItems.length} catalogue candidates: ${foodCount} food, ${cosmeticCount} cosmetic, ${northAmericanCount} US/CA/MX origin`)

      const response = await apiService.uploadToCatalogue(shipment.id, cataloguePassword, items)

      if (response.success) {
        setCatalogueResult({
          uploaded: response.uploaded || 0,
          updated: response.updated || 0,
          skipped: response.skipped || 0,
          failed: response.failed || 0,
          total: response.total || 0,
          results: response.results || []
        })
        console.log('✅ Catalogue upload complete:', response)
      } else {
        setCatalogueError(response.error || 'Failed to upload to catalogue')
        if (response.results) {
          setCatalogueResult({
            uploaded: response.uploaded || 0,
            updated: response.updated || 0,
            skipped: response.skipped || 0,
            failed: response.failed || 0,
            total: response.total || 0,
            results: response.results || []
          })
        }
      }
    } catch (error: any) {
      console.error('❌ Catalogue upload error:', error)
      setCatalogueError(error.message || 'Failed to upload to catalogue')
    } finally {
      setUploadingCatalogue(false)
    }
  }

  const handleServiceBreakdown = async (event?: MouseEvent<HTMLButtonElement>) => {
    if (event?.shiftKey) {
      await handleAddServices()
      return
    }

    try {
      setLoadingServiceBreakdown(true)
      const response = await apiService.getServiceBreakdown(shipment.id)
      if (response.success) {
        setServiceBreakdownData(response.data)
        setShowServiceBreakdown(true)
      } else {
        alert('Failed to get service breakdown: ' + (response.error || 'Unknown error'))
      }
    } catch (error: any) {
      console.error('Service breakdown error:', error)
      alert('Failed to get service breakdown: ' + (error.message || 'Unknown error'))
    } finally {
      setLoadingServiceBreakdown(false)
    }
  }

  const handleAddServices = async () => {
    try {
      setAddingServices(true)
      const response = await apiService.addServicesToShipment(shipment.id)

      if (response.success) {
        // Close the modal
        setShowServiceBreakdown(false)
        setServiceBreakdownData(null)

        // Show success message
        alert(`✅ Services added successfully!\n\n${response.data.servicesAdded} services have been added to the shipment.`)
      } else {
        alert('Failed to add services: ' + (response.error || 'Unknown error'))
      }
    } catch (error: any) {
      console.error('Add services error:', error)
      alert('Failed to add services: ' + (error.message || 'Unknown error'))
    } finally {
      setAddingServices(false)
    }
  }

  const handleGenerateInvoice = async (manualItems?: any[]) => {
    try {
      setGeneratingInvoice(true)
      setInvoiceError(null)

      // Cache the password for 2 hours
      passwordCache.set(invoicePassword)

      // Validate FC info if Direct to Amazon LTL
      if (ltlType === 'direct_amazon') {
        if (!fcId.trim()) {
          setInvoiceError('FC ID is required for Direct to Amazon LTL')
          setGeneratingInvoice(false)
          return
        }
        if (!fcAddress.street.trim() || !fcAddress.city.trim() ||
            !fcAddress.state.trim() || !fcAddress.zip.trim()) {
          setInvoiceError('Complete FC address is required for Direct to Amazon LTL')
          setGeneratingInvoice(false)
          return
        }
      }

      // Validate all items have required fields: COGS, Country of Origin, HTS Code
      const itemsWithMissingFields: string[] = []
      for (const item of items) {
        const missingFields: string[] = []

        if (!item.declaredValue || item.declaredValue <= 0) {
          missingFields.push('COGS')
        }
        if (!item.countryOfOrigin) {
          missingFields.push('Country of Origin')
        }
        if (!item.htsCode || item.htsCode.replace(/\D/g, '').length !== 10) {
          missingFields.push('HTS Code (10 digits)')
        }

        if (missingFields.length > 0) {
          const itemName = item.asin || item.description?.substring(0, 30) || 'Unknown item'
          itemsWithMissingFields.push(`• ${itemName}: Missing ${missingFields.join(', ')}`)
        }
      }

      if (itemsWithMissingFields.length > 0) {
        setInvoiceError(`Cannot generate invoice. The following items are missing required fields:\n\n${itemsWithMissingFields.join('\n')}`)
        setGeneratingInvoice(false)
        return
      }

      console.log('📝 Generating invoice with manual items:', manualItems)

      const response = await apiService.generateInvoice(
        shipment,
        items,
        invoicePassword,
        ltlType,
        brokerType,
        ltlType === 'direct_amazon' ? { fcId, fcAddress } : undefined,
        manualItems
      )

      console.log('✅ Invoice generation response:', response)

      if (response.success) {
        // Download the generated invoice
        if (response.fileData) {
          // Convert base64 to blob and download
          const byteCharacters = atob(response.fileData)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], { type: 'application/pdf' })
          
          const downloadUrl = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = downloadUrl
          link.download = response.filename || `commercial-invoice-${response.invoiceNumber}.pdf`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 60000)
        } else if (response.filename) {
          // Fallback to download endpoint
          await apiService.downloadInvoice(response.filename)
        }
        
        // Close the dialog and reset form
        setShowInvoiceDialog(false)
        setInvoicePassword('')
        setLtlType('direct_amazon')
        setFcId('')
        setFcAddress({ street: '', city: '', state: '', zip: '' })
        
        alert(`Commercial invoice generated successfully! (${response.invoiceNumber})`)
      } else {
        setInvoiceError(response.error || 'Failed to generate commercial invoice')
      }
    } catch (error: any) {
      console.error('❌ Invoice generation error:', error)
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      })
      setInvoiceError(error.message || 'Failed to generate commercial invoice')

      // Don't let auth callback redirect if we have an actual error to show
      if (error.message && !error.message.includes('session has expired')) {
        console.log('⚠️ Non-auth error, staying on page')
      }
    } finally {
      setGeneratingInvoice(false)
    }
  }

  // CUSMA Functions - REMOVED (form filling no longer needed)
  // CUSMA checkbox in ShipmentItemRow still works for duty calculations

  const getStatusBadge = (status: string) => {
    const baseStyle = {
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 'bold',
      textTransform: 'uppercase' as const
    }

    switch (status) {
      case 'pending':
        return { ...baseStyle, backgroundColor: '#fff3cd', color: '#856404' }
      case 'declared':
        return { ...baseStyle, backgroundColor: '#d1ecf1', color: '#0c5460' }
      case 'invoiced':
        return { ...baseStyle, backgroundColor: '#d4edda', color: '#155724' }
      default:
        return { ...baseStyle, backgroundColor: '#f8f9fa', color: '#6c757d' }
    }
  }

  const getDestinationText = () => {
    if (shipment.destinationType === 'amazon_fba') {
      return shipment.destinationDetails?.fcCode 
        ? `Amazon FBA (${shipment.destinationDetails.fcCode})`
        : 'Amazon FBA'
    }
    return 'Pattern Shipping'
  }

  if (loading) {
    return (
      <div className="loading">
        <div>Loading shipment details...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card">
        <button className="button secondary mb-4" onClick={onBack}>
          ← Back to Shipments
        </button>
        <div className="error">
          {error}
        </div>
        <button 
          className="button" 
          onClick={loadShipmentItems}
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="shipment-details">
      {/* Header */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <button className="button secondary" onClick={onBack}>
            ← Back to Shipments
          </button>
          <div style={getStatusBadge(shipment.status)}>
            {shipment.status}
          </div>
        </div>

        <h2 style={{ margin: '0 0 16px 0' }}>
          <a
            href={`https://app.yyzprep.ca/shipments/outbound/${shipment.id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#007bff',
              textDecoration: 'none',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
          >
            {shipment.reference}
          </a>
        </h2>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px',
          marginBottom: '16px'
        }}>
          <div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              CREATED
            </div>
            <div>
              {new Date(shipment.createdAt).toLocaleDateString()}
            </div>
          </div>

          {shipment.shippedAt && (
            <div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                SHIPPED
              </div>
              <div>
                {new Date(shipment.shippedAt).toLocaleDateString()}
              </div>
            </div>
          )}

          <div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              DESTINATION
            </div>
            <div>
              {getDestinationText()}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              ITEMS
            </div>
            <div>
              {items.length} item{items.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {shipment.notes && (
          <div style={{ 
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderLeft: '4px solid #007bff',
            borderRadius: '4px'
          }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              NOTES
            </div>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>
              {shipment.notes}
            </div>
          </div>
        )}
      </div>

      {/* Shipment Readiness Checklist */}
      {items.length > 0 && (
        <ShipmentChecklist
          shipment={shipment}
          items={items}
        />
      )}


      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="card" style={{ backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', color: '#721c24' }}>
          {validationErrors.map((error, index) => (
            <div key={index} style={{ marginBottom: '8px', fontWeight: 'bold' }}>
              ⚠️ {error}
            </div>
          ))}
        </div>
      )}

      {/* Items */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0 }}>
            Shipment Items
          </h3>
          <button
            onClick={() => setShowManualItemForm(!showManualItemForm)}
            style={{
              padding: '8px 14px',
              backgroundColor: '#0d6efd',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600
            }}
          >
            {showManualItemForm ? 'Cancel Manual Items' : '+ Add Manual Items'}
          </button>

          {/* Bulk Manufacturing Lookup Button */}
          {items.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={handleBulkMfgLookup}
                disabled={bulkMfgLookupProgress?.running || getItemsNeedingMfgLookup().length === 0}
                style={{
                  padding: '8px 16px',
                  backgroundColor: bulkMfgLookupProgress?.running ? '#6c757d' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: bulkMfgLookupProgress?.running || getItemsNeedingMfgLookup().length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  opacity: getItemsNeedingMfgLookup().length === 0 ? 0.6 : 1
                }}
                title={getItemsNeedingMfgLookup().length === 0
                  ? 'All items have matching manufacturing addresses or are missing country of origin'
                  : `Find manufacturing addresses for ${getItemsNeedingMfgLookup().length} item(s) (includes country mismatches)`}
              >
                {bulkMfgLookupProgress?.running ? (
                  <>Processing...</>
                ) : (
                  <>Auto-Select Manufacturing ({getItemsNeedingMfgLookup().length}/{items.length})</>
                )}
              </button>

              {/* Bulk HTS Classification Button */}
              <button
                onClick={handleBulkHTSClassification}
                disabled={bulkHTSProgress?.running || bulkMfgLookupProgress?.running || getItemsNeedingHTSClassification().length === 0}
                style={{
                  padding: '8px 16px',
                  backgroundColor: bulkHTSProgress?.running ? '#6c757d' : '#6f42c1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: bulkHTSProgress?.running || bulkMfgLookupProgress?.running || getItemsNeedingHTSClassification().length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  opacity: getItemsNeedingHTSClassification().length === 0 ? 0.6 : 1
                }}
                title={getItemsNeedingHTSClassification().length === 0
                  ? 'All items already have HTS codes'
                  : `Auto-classify HTS codes for ${getItemsNeedingHTSClassification().length} item(s) - selects cheapest valid applicable option, preferring 80%+ confidence and allowing 70%+ fallback`}
              >
                {bulkHTSProgress?.running ? (
                  <>Classifying...</>
                ) : (
                  <>Auto-Select HTS ({getItemsNeedingHTSClassification().length}/{items.length})</>
                )}
              </button>

              {/* Bulk FDA Classification Button */}
              <button
                onClick={handleBulkFDAClassification}
                disabled={bulkFDAProgress?.running || bulkHTSProgress?.running || bulkMfgLookupProgress?.running || getItemsNeedingFDAClassification().length === 0}
                style={{
                  padding: '8px 16px',
                  backgroundColor: bulkFDAProgress?.running ? '#6c757d' : '#0d9488',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: bulkFDAProgress?.running || bulkHTSProgress?.running || bulkMfgLookupProgress?.running || getItemsNeedingFDAClassification().length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  opacity: getItemsNeedingFDAClassification().length === 0 ? 0.6 : 1
                }}
                title={getItemsNeedingFDAClassification().length === 0
                  ? 'All items already classified as food or cosmetic'
                  : `Auto-classify FDA type for ${getItemsNeedingFDAClassification().length} item(s) - determines if food, cosmetic, or neither`}
              >
                {bulkFDAProgress?.running ? (
                  <>Classifying...</>
                ) : (
                  <>Auto-Classify FDA ({getItemsNeedingFDAClassification().length}/{items.length})</>
                )}
              </button>

              {/* Bulk SSB Rate Check Button */}
              <button
                onClick={handleBulkSSBRateCheck}
                disabled={bulkSSBProgress?.running || bulkFDAProgress?.running || bulkHTSProgress?.running || bulkMfgLookupProgress?.running || getItemsEligibleForSSBRateCheck().length === 0}
                style={{
                  padding: '8px 16px',
                  backgroundColor: bulkSSBProgress?.running ? '#6c757d' : '#1565c0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: bulkSSBProgress?.running || bulkFDAProgress?.running || bulkHTSProgress?.running || bulkMfgLookupProgress?.running || getItemsEligibleForSSBRateCheck().length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  opacity: getItemsEligibleForSSBRateCheck().length === 0 ? 0.6 : 1
                }}
                title={getItemsEligibleForSSBRateCheck().length === 0
                  ? 'No items eligible - need 10-digit HTS, Country of Origin, and COGS > 0'
                  : `Check SSB duty rates for ${getItemsEligibleForSSBRateCheck().length} eligible item(s)`}
              >
                {bulkSSBProgress?.running ? (
                  <>Checking Rates...</>
                ) : (
                  <>Bulk SSB Rates ({getItemsEligibleForSSBRateCheck().length}/{items.length})</>
                )}
              </button>

              {/* Warning for items missing country of origin (for Manufacturing) */}
              {getItemsMissingCOO().length > 0 && (
                <div
                  style={{
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    color: '#856404',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    maxWidth: '500px'
                  }}
                  title={getItemsMissingCOO().map(item => item.asin || item.description?.substring(0, 20)).join(', ')}
                >
                  <strong>Note:</strong> {getItemsMissingCOO().length} item(s) missing Country of Origin will be skipped for Manufacturing auto-select
                </div>
              )}
            </div>
          )}
        </div>

        {showManualItemForm && (
          <div style={{ border: '1px solid #cfe2ff', backgroundColor: '#f8fbff', borderRadius: '6px', padding: '14px', marginBottom: '16px' }}>
            <div style={{ fontWeight: 700, marginBottom: '8px', color: '#084298' }}>Manual shipment items</div>
            <textarea
              value={manualItemsText}
              onChange={(e) => setManualItemsText(e.target.value)}
              placeholder={'One item per line, ending with quantity. Example:\nDUREX SENSI-THIN 12PK - CASE 10 OF 48 PER CASE 480 UNITS'}
              rows={6}
              style={{ width: '100%', padding: '10px', border: '1px solid #b6d4fe', borderRadius: '4px', fontSize: '13px', fontFamily: 'inherit', resize: 'vertical' }}
            />
            {manualItemError && (
              <div style={{ color: '#842029', backgroundColor: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: '4px', padding: '8px 10px', marginTop: '8px', fontSize: '13px' }}>
                {manualItemError}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '10px' }}>
              <button
                onClick={() => { setShowManualItemForm(false); setManualItemError(null) }}
                disabled={addingManualItems}
                style={{ padding: '7px 12px', border: '1px solid #ced4da', backgroundColor: '#fff', borderRadius: '4px', cursor: addingManualItems ? 'not-allowed' : 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddManualItems}
                disabled={addingManualItems || !manualItemsText.trim()}
                style={{ padding: '7px 12px', border: 'none', backgroundColor: addingManualItems || !manualItemsText.trim() ? '#6c757d' : '#198754', color: '#fff', borderRadius: '4px', cursor: addingManualItems || !manualItemsText.trim() ? 'not-allowed' : 'pointer', fontWeight: 700 }}
              >
                {addingManualItems ? 'Adding...' : 'Add Items'}
              </button>
            </div>
          </div>
        )}

        {bulkCOOProgress && (
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: bulkCOOProgress.manualReview > 0 ? '#fff7ed' : '#ecfdf5',
            border: `1px solid ${bulkCOOProgress.manualReview > 0 ? '#fb923c' : '#6ee7b7'}`,
            borderRadius: '8px'
          }}>
            <div style={{ marginBottom: '12px', fontWeight: 'bold', color: bulkCOOProgress.manualReview > 0 ? '#9a3412' : '#065f46' }}>
              YYZ Country of Origin Check {bulkCOOProgress.running ? 'in Progress' : 'Complete'}
            </div>
            <div style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#dee2e6',
              borderRadius: '4px',
              marginBottom: '12px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${bulkCOOProgress.totalItems ? (bulkCOOProgress.currentItem / bulkCOOProgress.totalItems) * 100 : 0}%`,
                height: '100%',
                backgroundColor: bulkCOOProgress.manualReview > 0 ? '#f97316' : '#0f766e',
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ fontSize: '14px', color: '#495057' }}>
              <div style={{ marginBottom: '4px' }}>
                <strong>Progress:</strong> {bulkCOOProgress.currentItem} / {bulkCOOProgress.totalItems} items
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Current:</strong> {bulkCOOProgress.currentItemName || 'Starting...'}
              </div>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <span style={{ color: '#15803d' }}>Filled blanks: {bulkCOOProgress.filled}</span>
                <span style={{ color: '#15803d' }}>Already matched: {bulkCOOProgress.matched}</span>
                <span style={{ color: '#c2410c', fontWeight: 700 }}>Manual review: {bulkCOOProgress.manualReview}</span>
                <span style={{ color: '#6b7280' }}>No YYZ suggestion: {bulkCOOProgress.noSuggestion}</span>
                <span style={{ color: '#dc2626' }}>Failed: {bulkCOOProgress.failed}</span>
              </div>
              {bulkCOOProgress.reviewItems.length > 0 && (
                <div style={{ marginTop: '12px', padding: '10px', backgroundColor: '#ffedd5', border: '1px solid #fdba74', borderRadius: '6px' }}>
                  <div style={{ fontWeight: 700, color: '#9a3412', marginBottom: '6px' }}>
                    Manual review required. These existing COO values were not changed:
                  </div>
                  {bulkCOOProgress.reviewItems.map(reviewItem => (
                    <div key={reviewItem.itemId} style={{ fontSize: '12px', color: '#7c2d12', marginBottom: '4px' }}>
                      <strong>{reviewItem.label}</strong>: current {reviewItem.current}, YYZ recommends {reviewItem.recommended}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bulk Manufacturing Lookup Progress */}
        {bulkMfgLookupProgress && (
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: '#e7f3ff',
            border: '1px solid #b6d4fe',
            borderRadius: '8px'
          }}>
            <div style={{ marginBottom: '12px', fontWeight: 'bold', color: '#0d6efd' }}>
              AI Manufacturing Lookup in Progress
            </div>

            {/* Progress bar */}
            <div style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#dee2e6',
              borderRadius: '4px',
              marginBottom: '12px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${(bulkMfgLookupProgress.currentItem / bulkMfgLookupProgress.totalItems) * 100}%`,
                height: '100%',
                backgroundColor: '#0d6efd',
                transition: 'width 0.3s ease'
              }} />
            </div>

            <div style={{ fontSize: '14px', color: '#495057' }}>
              <div style={{ marginBottom: '4px' }}>
                <strong>Progress:</strong> {bulkMfgLookupProgress.currentItem} / {bulkMfgLookupProgress.totalItems} items
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Current:</strong> {bulkMfgLookupProgress.currentItemName || 'Starting...'}
              </div>
              <div style={{ display: 'flex', gap: '20px' }}>
                <span style={{ color: '#198754' }}>
                  Found: {bulkMfgLookupProgress.completed}
                </span>
                <span
                  style={{
                    color: '#dc3545',
                    cursor: bulkMfgLookupProgress.failedItems.length > 0 ? 'help' : 'default'
                  }}
                  title={bulkMfgLookupProgress.failedItems.length > 0
                    ? `Failed items:\n${bulkMfgLookupProgress.failedItems.join('\n')}`
                    : undefined}
                >
                  Not found: {bulkMfgLookupProgress.failed}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Bulk HTS Classification Progress */}
        {bulkHTSProgress && (
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: '#f3e8ff',
            border: '1px solid #d8b4fe',
            borderRadius: '8px'
          }}>
            <div style={{ marginBottom: '12px', fontWeight: 'bold', color: '#6b21a8' }}>
              AI HTS Classification in Progress
            </div>

            {/* Progress bar */}
            <div style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#dee2e6',
              borderRadius: '4px',
              marginBottom: '12px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${(bulkHTSProgress.currentItem / bulkHTSProgress.totalItems) * 100}%`,
                height: '100%',
                backgroundColor: '#6f42c1',
                transition: 'width 0.3s ease'
              }} />
            </div>

            <div style={{ fontSize: '14px', color: '#495057' }}>
              <div style={{ marginBottom: '4px' }}>
                <strong>Progress:</strong> {bulkHTSProgress.currentItem} / {bulkHTSProgress.totalItems} items
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Current:</strong> {bulkHTSProgress.currentItemName || 'Starting...'}
              </div>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <span style={{ color: '#198754' }}>
                  Auto-selected: {bulkHTSProgress.completed}
                </span>
                <span
                  style={{
                    color: '#f59e0b',
                    cursor: bulkHTSProgress.skippedItems.length > 0 ? 'help' : 'default'
                  }}
                  title={bulkHTSProgress.skippedItems.length > 0
                    ? `Skipped items (no valid applicable code with at least 70% confidence):\n${bulkHTSProgress.skippedItems.join('\n')}`
                    : undefined}
                >
                  Skipped: {bulkHTSProgress.skipped}
                </span>
                <span
                  style={{
                    color: '#dc3545',
                    cursor: bulkHTSProgress.failedItems.length > 0 ? 'help' : 'default'
                  }}
                  title={bulkHTSProgress.failedItems.length > 0
                    ? `Failed items:\n${bulkHTSProgress.failedItems.join('\n')}`
                    : undefined}
                >
                  Failed: {bulkHTSProgress.failed}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Bulk FDA Classification Progress */}
        {bulkFDAProgress && (
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: '#ecfdf5',
            border: '1px solid #6ee7b7',
            borderRadius: '8px'
          }}>
            <div style={{ marginBottom: '12px', fontWeight: 'bold', color: '#065f46' }}>
              AI FDA Classification in Progress
            </div>

            {/* Progress bar */}
            <div style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#dee2e6',
              borderRadius: '4px',
              marginBottom: '12px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${(bulkFDAProgress.currentItem / bulkFDAProgress.totalItems) * 100}%`,
                height: '100%',
                backgroundColor: '#0d9488',
                transition: 'width 0.3s ease'
              }} />
            </div>

            <div style={{ fontSize: '14px', color: '#495057' }}>
              <div style={{ marginBottom: '4px' }}>
                <strong>Progress:</strong> {bulkFDAProgress.currentItem} / {bulkFDAProgress.totalItems} items
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Current:</strong> {bulkFDAProgress.currentItemName || 'Starting...'}
              </div>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <span style={{ color: '#b91c1c' }}>
                  🍎 Food: {bulkFDAProgress.foodCount}
                </span>
                <span style={{ color: '#be185d' }}>
                  💄 Cosmetic: {bulkFDAProgress.cosmeticCount}
                </span>
                <span style={{ color: '#6b7280' }}>
                  ⏭️ Neither: {bulkFDAProgress.neitherCount}
                </span>
                <span
                  style={{
                    color: '#dc3545',
                    cursor: bulkFDAProgress.failedItems.length > 0 ? 'help' : 'default'
                  }}
                  title={bulkFDAProgress.failedItems.length > 0
                    ? `Failed items:\n${bulkFDAProgress.failedItems.join('\n')}`
                    : undefined}
                >
                  Failed: {bulkFDAProgress.failed}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Bulk SSB Rate Check Progress */}
        {bulkSSBProgress && (
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: '#e3f2fd',
            border: '1px solid #90caf9',
            borderRadius: '8px'
          }}>
            <div style={{ marginBottom: '12px', fontWeight: 'bold', color: '#1565c0' }}>
              SSB Rate Verification in Progress
            </div>

            {/* Progress bar */}
            <div style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#dee2e6',
              borderRadius: '4px',
              marginBottom: '12px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${(bulkSSBProgress.currentItem / bulkSSBProgress.totalItems) * 100}%`,
                height: '100%',
                backgroundColor: '#1565c0',
                transition: 'width 0.3s ease'
              }} />
            </div>

            <div style={{ fontSize: '14px', color: '#495057' }}>
              <div style={{ marginBottom: '4px' }}>
                <strong>Progress:</strong> {bulkSSBProgress.currentItem} / {bulkSSBProgress.totalItems} items
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Current:</strong> {bulkSSBProgress.currentItemName || 'Starting...'}
              </div>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <span style={{ color: '#198754' }}>
                  Verified: {bulkSSBProgress.completed}
                </span>
                <span
                  style={{
                    color: '#dc3545',
                    cursor: bulkSSBProgress.failedItems.length > 0 ? 'help' : 'default'
                  }}
                  title={bulkSSBProgress.failedItems.length > 0
                    ? `Failed items:\n${bulkSSBProgress.failedItems.join('\n')}`
                    : undefined}
                >
                  Failed: {bulkSSBProgress.failed}
                </span>
              </div>
            </div>
          </div>
        )}

        {items.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#666',
            fontStyle: 'italic'
          }}>
            No items found in this shipment.
          </div>
        ) : (
          <div style={{ overflowX: 'auto', position: 'relative' }}>
            {/* Table Header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: SHIPMENT_TABLE_GRID,
              backgroundColor: '#f8f9fa',
              borderBottom: '2px solid #dee2e6',
              padding: '8px 0',
              fontSize: '10px',
              fontWeight: 700,
              color: '#495057',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              position: 'sticky',
              top: 0,
              zIndex: 10,
              minWidth: '1360px'
            }}>
              <div style={{ padding: '0 6px' }}></div>
              <div style={{ padding: '0 6px' }}>Product</div>
              <div style={{ padding: '0 4px', textAlign: 'center' }}>Qty</div>
              <div style={{ padding: '0 4px' }}>Country</div>
              <div style={{ padding: '0 4px' }}>HTS Code</div>
              <div style={{ padding: '0 4px' }}>COGS (CAD)</div>
              <div style={{ padding: '0 4px' }}>Duty / SSB</div>
              <div style={{ padding: '0 4px' }}>Total Duties</div>
              <div style={{ padding: '0 4px' }}>Type / FDA</div>
              <div style={{ padding: '0 4px' }}>Description</div>
              <div style={{ padding: '0 4px' }}>Manufacturer</div>
              <div style={{ padding: '0 4px' }}>Food Reg ID</div>
              <div></div>
            </div>
            <div style={{ minWidth: '1360px' }}>
            {items.map((item) => (
              <ShipmentItemRow
                key={item.id}
                item={item}
                shipmentId={shipment.id}
                manufacturingAddresses={manufacturingAddresses}
                validationErrors={itemValidationErrors[item.id] || []}
                exchangeRate={exchangeRate}
                externalSsbVerifiedData={bulkSsbResults[item.id] || null}
                onSSBRateCalculated={(result) => {
                  setBulkSsbResults(prev => {
                    const next = { ...prev }
                    if (result) {
                      next[item.id] = result
                    } else {
                      delete next[item.id]
                    }
                    return next
                  })
                }}
                onUpdate={async (updates) => {
                  // Debug: Log what updates are being applied
                  if (updates.customsDescription !== undefined) {
                    console.log(`📝 ShipmentDetails onUpdate for ${item.asin}: customsDescription = "${updates.customsDescription?.substring(0, 30)}..."`)
                  }

                  // Update the item in the items array immediately for UI responsiveness
                  setItems(prevItems =>
                    prevItems.map(prevItem => {
                      if (prevItem.id !== item.id) return prevItem

                      const nextItem = { ...prevItem, ...updates }
                      if (updates.countryOfOrigin !== undefined && nextItem.yyzRecommendedCountryOfOrigin) {
                        const current = normalizeCOO(updates.countryOfOrigin)
                        const recommended = normalizeCOO(nextItem.yyzRecommendedCountryOfOrigin)

                        if (current && current === recommended) {
                          return {
                            ...nextItem,
                            cooReviewStatus: 'matched',
                            cooReviewMessage: null
                          }
                        }

                        return {
                          ...nextItem,
                          cooReviewStatus: 'manual_review',
                          cooReviewMessage: `Manual review: current ${current || 'blank'} does not match YYZ ${recommended}`
                        }
                      }

                      return nextItem
                    })
                  )
                  
                  // Save to backend database (debounced in ShipmentItemRow)
                  try {
                    if (updates.customsDescription !== undefined && item.asin) {
                      await apiService.saveClientASINData(item.asin, {
                        customs_description: updates.customsDescription || null,
                        outbound_id: shipment.id,
                        change_source: 'manual_ui'
                      })
                    }

                    const response = await apiService.updateShipmentItem(shipment.id, item.id, updates)
                    if (response.success) {
                      
                      // CUSMA recheck removed - form filling no longer needed
                    } else {
                      console.error(`❌ Failed to update item ${item.id} in database:`, response.error)
                    }
                  } catch (error) {
                    console.error(`❌ Error updating item ${item.id} in database:`, error)
                  }
                }}
                onNewAddressSaved={handleNewAddressSaved}
              />
            ))}
            </div>
          </div>
        )}
        
        {/* Shipment Totals */}
        {(() => {
          const totals = calculateTotals()
          if (!totals) return null
          
          return (
            <div style={{
              marginTop: '20px',
              padding: '16px',
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '4px'
            }}>
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '12px'
                }}>
                  <span>Total Shipment Value (COGS):</span>
                  <div style={{ textAlign: 'right' }}>
                    <div>${totals.totalCAD.toFixed(2)} CAD</div>
                    <div style={{
                      fontSize: '14px',
                      color: '#666',
                      fontWeight: 'normal'
                    }}>
                      (${totals.totalUSD.toFixed(2)} USD)
                    </div>
                  </div>
                </div>

                {totals.totalDutiesCAD > 0 && (
                  <>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      paddingTop: '12px',
                      borderTop: '1px solid #dee2e6'
                    }}>
                      <span>SSB Total Duties + Tariffs:</span>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: '#dc2626' }}>${totals.totalDutiesCAD.toFixed(2)} CAD</div>
                        <div style={{
                          fontSize: '14px',
                          color: '#666',
                          fontWeight: 'normal'
                        }}>
                          ({((totals.totalDutiesCAD / totals.totalCAD) * 100).toFixed(2)}% of COGS)
                        </div>
                      </div>
                    </div>
                    <div style={{
                      marginTop: '12px',
                      padding: '12px',
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffc107',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#856404',
                      lineHeight: '1.6'
                    }}>
                      <strong>Disclaimer:</strong> Duty and tariff totals use SSB-verified rates only. SSB rates are cached server-side for 12 hours and recalculated from each item's CAD COGS. CBP can still make the final duty determination at import.
                    </div>
                  </>
                )}

                {ssbRatesLoading && (
                  <div style={{
                    marginTop: '12px',
                    padding: '10px 12px',
                    backgroundColor: '#e3f2fd',
                    border: '1px solid #90caf9',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#1565c0',
                    fontWeight: 600
                  }}>
                    Loading SSB duty rates for {totals.ssbEligibleItemCount} eligible item(s)...
                  </div>
                )}

                {!ssbRatesLoading && totals.ssbEligibleItemCount > totals.ssbRatedItemCount && (
                  <div style={{
                    marginTop: '12px',
                    padding: '10px 12px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#856404'
                  }}>
                    SSB duties loaded for {totals.ssbRatedItemCount} / {totals.ssbEligibleItemCount} eligible item(s). Use Bulk SSB Rates to retry missing rates.
                  </div>
                )}

                {ssbRatesError && (
                  <div style={{
                    marginTop: '12px',
                    padding: '10px 12px',
                    backgroundColor: '#f8d7da',
                    border: '1px solid #f5c6cb',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#721c24',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {ssbRatesError}
                  </div>
                )}
              </div>
            </div>
          )
        })()}
      </div>

      {/* Action Buttons */}
      {items.length > 0 && (
        <div className="card">
          <div style={{
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#6c757d',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '16px',
            paddingBottom: '8px',
            borderBottom: '2px solid #dee2e6'
          }}>
            For Internal YYZ Prep Staff Use Only
          </div>
          <div className="flex gap-4">
            <div>
              <button
                className="button"
                onClick={() => setShowInvoiceDialog(true)}
              >
                Generate Commercial Invoice
              </button>
            </div>
            <div>
              <button
                className="button"
                style={{ backgroundColor: '#17a2b8' }}
                onClick={openSSBDialog}
              >
                Submit to SSB
              </button>
            </div>
            <div>
              <button
                className="button"
                style={{ backgroundColor: '#6f42c1' }}
                onClick={() => setShowSSBCDialog(true)}
              >
                Generate SSBC
              </button>
            </div>
            <div>
              <button
                className="button"
                style={{ backgroundColor: '#ff6b35' }}
                onClick={() => setShowCatalogueDialog(true)}
              >
                Upload to Catalogue
              </button>
            </div>
            <div>
              <button
                className="button"
                style={{ backgroundColor: '#28a745' }}
                onClick={handleServiceBreakdown}
                disabled={loadingServiceBreakdown || addingServices}
                title="Shift-click to add services directly"
              >
                {addingServices ? 'Adding Services...' : loadingServiceBreakdown ? 'Loading...' : 'Service Breakdown'}
              </button>
            </div>

            {shipment.status === 'pending' && (
              <button
                className="button secondary"
                onClick={handleRunAILookup}
                disabled={loading}
              >
                {loading ? 'Running AI Lookup...' : 'Run AI Lookup'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Commercial Invoice Editor Dialog */}
      <InvoiceEditorDialog
        isOpen={showInvoiceDialog}
        onClose={() => {
          setShowInvoiceDialog(false)
          setInvoicePassword('')
          setInvoiceError(null)
          setBrokerType('clearit')
          setLtlType('direct_amazon')
          setFcId('')
          setFcAddress({ street: '', city: '', state: '', zip: '' })
        }}
        shipment={shipment}
        items={items}
        onGenerate={handleGenerateInvoice}
        ltlType={ltlType}
        brokerType={brokerType}
        fcId={fcId}
        fcAddress={fcAddress}
        setLtlType={setLtlType}
        setBrokerType={setBrokerType}
        setFcId={setFcId}
        setFcAddress={setFcAddress}
        invoicePassword={invoicePassword}
        setInvoicePassword={setInvoicePassword}
        generatingInvoice={generatingInvoice}
        invoiceError={invoiceError}
      />


      {/* SSB Submission Dialog */}
      {showSSBDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 20px 0' }}>
              Submit to SSB
            </h3>

            <div style={{
              backgroundColor: '#d1ecf1',
              border: '1px solid #bee5eb',
              color: '#0c5460',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              <strong>SSB Submission - Internal Use Only</strong><br />
              This will submit your shipment for processing. All HTS codes and package information will be validated before submission.
            </div>

            {ssbError && (
              <div style={{
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                color: '#721c24',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '16px',
                whiteSpace: 'pre-wrap'
              }}>
                {ssbError}
              </div>
            )}

            {catalogueFlagIssues.length > 0 && (
              <div style={{ marginBottom: '18px' }}>
                <div style={{ fontWeight: 700, marginBottom: '8px', color: '#721c24' }}>
                  Products requiring ShipSavvy catalogue updates
                </div>
                {catalogueFlagIssues.map((issue) => (
                  <div
                    key={issue.catalogueUuid}
                    style={{
                      border: '1px solid #f0ad4e',
                      backgroundColor: '#fff8e5',
                      borderRadius: '6px',
                      padding: '12px',
                      marginBottom: '10px'
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>
                      {issue.asin}{issue.sku && issue.sku !== issue.asin ? ` · SKU ${issue.sku}` : ''}
                    </div>
                    {issue.description && (
                      <div style={{ fontSize: '13px', color: '#555', marginTop: '3px' }}>
                        {issue.description}
                      </div>
                    )}
                    <div style={{ fontSize: '13px', marginTop: '7px' }}>
                      U.S. flag: <strong>Disabled</strong>
                      {issue.usFlagRequested ? ' (approval requested)' : ' (approval not requested)'}
                    </div>
                    <div style={{ fontSize: '13px', marginTop: '5px' }}>
                      {issue.missingFields.length > 0 ? (
                        <>Missing or incomplete: {issue.missingFields.map(field => field.label).join(', ')}.</>
                      ) : (
                        <>ShipSavvy did not return a field-level reason. Review the U.S. approval and PGA sections.</>
                      )}
                    </div>
                    <button
                      type="button"
                      className="button"
                      style={{ marginTop: '10px', backgroundColor: '#d97706', fontSize: '13px' }}
                      onClick={() => window.open(issue.editUrl, '_blank', 'noopener,noreferrer')}
                    >
                      Open {issue.asin} in ShipSavvy
                    </button>
                  </div>
                ))}
                <div style={{ fontSize: '13px', color: '#555' }}>
                  Save the catalogue changes in ShipSavvy, then click Submit to SSB again to recheck every product.
                </div>
              </div>
            )}

            {ssbHTSStatuses.length > 0 && (
              <div style={{ marginBottom: '18px' }}>
                <div style={{ fontWeight: 700, marginBottom: '8px' }}>HTS submission breakdown</div>
                {ssbHTSStatuses.map((entry, index) => {
                  const accepted = entry.status === 'accepted'
                  return (
                    <div key={`${entry.asin || 'item'}-${index}`} style={{
                      border: `1px solid ${accepted ? '#86c99a' : entry.status === 'rejected' ? '#e2a0a7' : '#e5c36b'}`,
                      background: accepted ? '#edf9f0' : entry.status === 'rejected' ? '#fff1f2' : '#fff9e8',
                      borderRadius: '6px', padding: '10px', marginBottom: '8px', fontSize: '13px'
                    }}>
                      <div style={{ fontWeight: 700 }}>{entry.asin || 'Unidentified item'} · {entry.status.toUpperCase()}</div>
                      {entry.description && <div style={{ color: '#555', marginTop: '2px' }}>{entry.description}</div>}
                      <div style={{ marginTop: '5px' }}>
                        {entry.rejectedHts && <>Rejected/previous HTS: <strong>{entry.rejectedHts}</strong></>}
                        {entry.acceptedHts && <> → Accepted/current HTS: <strong>{entry.acceptedHts}</strong></>}
                      </div>
                      {entry.shipments.length > 0 && <div>Applied to: {entry.shipments.join(', ')}</div>}
                      {entry.message && <div style={{ marginTop: '3px' }}>{entry.message}</div>}
                    </div>
                  )
                })}
              </div>
            )}

            {trustedActionDevice ? (
              <div style={{
                backgroundColor: '#e8f5e9',
                border: '1px solid #a5d6a7',
                color: '#1b5e20',
                padding: '10px 12px',
                borderRadius: '4px',
                marginBottom: '20px',
                fontSize: '14px'
              }}>
                This computer is trusted for SSB submissions. No password required.
              </div>
            ) : (
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}>
                  Password
                  {cachedPasswordMinutes > 0 && (
                    <span style={{ fontWeight: 'normal', color: '#28a745', marginLeft: '8px', fontSize: '12px' }}>
                      (cached for {cachedPasswordMinutes} min)
                    </span>
                  )}
                </label>
                <input
                  type="password"
                  value={ssbPassword}
                  onChange={(e) => setSSBPassword(e.target.value)}
                  placeholder="Enter password to submit to SSB"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: cachedPasswordMinutes > 0 ? '1px solid #28a745' : '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: cachedPasswordMinutes > 0 ? '#f8fff8' : '#fff'
                  }}
                />
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                fontSize: '14px'
              }}>
                <input
                  type="checkbox"
                  checked={skipTracking}
                  onChange={(e) => setSkipTracking(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <span>
                  <strong>Buy label from SSB</strong>
                  <span style={{ color: '#666', marginLeft: '8px' }}>
                    (Skip tracking codes - use when purchasing labels through SSB)
                  </span>
                </span>
              </label>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                className="button secondary"
                onClick={() => {
                  setShowSSBDialog(false)
                  setSSBPassword('')
                  setSSBError(null)
                  setCatalogueFlagIssues([])
                  setSSBHTSStatuses([])
                  setSkipTracking(false)
                }}
                disabled={submittingSSB}
              >
                Cancel
              </button>
              <button
                className="button"
                style={{ backgroundColor: '#17a2b8' }}
                onClick={handleSubmitToSSB}
                disabled={submittingSSB || (!trustedActionDevice && !ssbPassword.trim())}
              >
                {submittingSSB ? (ssbProgress || 'Submitting...') : 'Submit to SSB'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SSBC Generation Dialog */}
      {showSSBCDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 20px 0' }}>
              Generate SSBC
            </h3>

            <div style={{
              backgroundColor: '#f3e8ff',
              border: '1px solid #d8b4fe',
              color: '#6b21a8',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              <strong>SSBC Excel Generation - Internal Use Only</strong><br />
              This will generate an Excel file with:
              <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
                <li>Product descriptions and FDA codes</li>
                <li>Manufacturer information</li>
                <li>FDA program codes (FOO/COS)</li>
                <li>Contact and packaging details</li>
              </ul>
            </div>

            {ssbcError && (
              <div style={{
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                color: '#721c24',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '16px'
              }}>
                {ssbcError}
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 'bold',
                fontSize: '14px'
              }}>
                Password
                {cachedPasswordMinutes > 0 && (
                  <span style={{ fontWeight: 'normal', color: '#28a745', marginLeft: '8px', fontSize: '12px' }}>
                    (cached for {cachedPasswordMinutes} min)
                  </span>
                )}
              </label>
              <input
                type="password"
                value={ssbcPassword}
                onChange={(e) => setSsbcPassword(e.target.value)}
                placeholder="Enter password to generate SSBC"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: cachedPasswordMinutes > 0 ? '1px solid #28a745' : '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: cachedPasswordMinutes > 0 ? '#f8fff8' : '#fff'
                }}
              />
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                className="button secondary"
                onClick={() => {
                  setShowSSBCDialog(false)
                  setSsbcPassword('')
                  setSsbcError(null)
                }}
                disabled={generatingSSBC}
              >
                Cancel
              </button>
              <button
                className="button"
                style={{ backgroundColor: '#6f42c1' }}
                onClick={handleGenerateSSBC}
                disabled={generatingSSBC}
              >
                {generatingSSBC ? 'Generating...' : 'Generate SSBC Excel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Catalogue Upload Dialog */}
      {showCatalogueDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 20px 0' }}>
              Upload to SSB Catalogue
            </h3>

            <div style={{
              backgroundColor: '#fff3e0',
              border: '1px solid #ffb74d',
              color: '#e65100',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              <strong>FDA/Food/Cosmetic Product Upload</strong><br />
              This will upload FDA-regulated products to the SSB product catalogue.
              <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
                <li>Only products marked as food/FDA/cosmetic will be uploaded</li>
                <li>Products already in the catalogue will be skipped</li>
                <li>Includes HTS codes, FDA codes, and manufacturer info</li>
              </ul>
            </div>

            {catalogueError && (
              <div style={{
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                color: '#721c24',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '16px'
              }}>
                {catalogueError}
              </div>
            )}

            {catalogueResult && (
              <div style={{
                backgroundColor: catalogueResult.failed > 0 ? '#fff3cd' : '#d4edda',
                border: `1px solid ${catalogueResult.failed > 0 ? '#ffc107' : '#28a745'}`,
                color: catalogueResult.failed > 0 ? '#856404' : '#155724',
                padding: '16px',
                borderRadius: '4px',
                marginBottom: '16px'
              }}>
                <strong>Upload Complete</strong>
                <div style={{ marginTop: '8px' }}>
                  <div>Total processed: {catalogueResult.total}</div>
                  <div style={{ color: '#28a745' }}>Uploaded: {catalogueResult.uploaded}</div>
                  {catalogueResult.updated > 0 && (
                    <div style={{ color: '#0d6efd' }}>Updated: {catalogueResult.updated}</div>
                  )}
                  <div style={{ color: '#6c757d' }}>Skipped (no changes): {catalogueResult.skipped}</div>
                  {catalogueResult.failed > 0 && (
                    <div style={{ color: '#dc3545' }}>Failed: {catalogueResult.failed}</div>
                  )}
                </div>

                {catalogueResult.results && catalogueResult.results.length > 0 && (
                  <div style={{ marginTop: '12px', fontSize: '12px' }}>
                    <strong>Details:</strong>
                    <div style={{ maxHeight: '150px', overflow: 'auto', marginTop: '8px' }}>
                      {catalogueResult.results.map((r, idx) => (
                        <div key={idx} style={{
                          padding: '4px 8px',
                          backgroundColor: r.status === 'uploaded' ? '#e8f5e9' : r.status === 'updated' ? '#e3f2fd' : r.status === 'skipped' ? '#f5f5f5' : '#ffebee',
                          borderRadius: '4px',
                          marginBottom: '4px'
                        }}>
                          <span style={{ fontFamily: 'monospace' }}>{r.asin}</span>
                          <span style={{
                            marginLeft: '8px',
                            color: r.status === 'uploaded' ? '#2e7d32' : r.status === 'updated' ? '#1565c0' : r.status === 'skipped' ? '#757575' : '#c62828'
                          }}>
                            {r.status === 'uploaded' ? 'Uploaded' : r.status === 'updated' ? 'Updated' : r.status === 'skipped' ? 'Skipped' : 'Failed'}
                          </span>
                          {r.error && <span style={{ marginLeft: '8px', color: '#c62828' }}>({r.error})</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!catalogueResult && (
              trustedActionDevice ? (
                <div style={{
                  backgroundColor: '#e8f5e9',
                  border: '1px solid #a5d6a7',
                  color: '#1b5e20',
                  padding: '10px 12px',
                  borderRadius: '4px',
                  marginBottom: '20px',
                  fontSize: '14px'
                }}>
                  This computer is trusted for catalogue uploads. No password required.
                </div>
              ) : (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}>
                    Password
                    {cachedPasswordMinutes > 0 && (
                      <span style={{ fontWeight: 'normal', color: '#28a745', marginLeft: '8px', fontSize: '12px' }}>
                        (cached for {cachedPasswordMinutes} min)
                      </span>
                    )}
                  </label>
                  <input
                    type="password"
                    value={cataloguePassword}
                    onChange={(e) => setCataloguePassword(e.target.value)}
                    placeholder="Enter password to upload"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: cachedPasswordMinutes > 0 ? '1px solid #28a745' : '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: cachedPasswordMinutes > 0 ? '#f8fff8' : '#fff'
                    }}
                  />
                </div>
              )
            )}

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                className="button secondary"
                onClick={() => {
                  setShowCatalogueDialog(false)
                  setCataloguePassword('')
                  setCatalogueError(null)
                  setCatalogueResult(null)
                }}
                disabled={uploadingCatalogue}
              >
                {catalogueResult ? 'Close' : 'Cancel'}
              </button>
              {!catalogueResult && (
                <button
                  className="button"
                  style={{ backgroundColor: '#ff6b35' }}
                  onClick={handleUploadToCatalogue}
                  disabled={uploadingCatalogue || (!trustedActionDevice && !cataloguePassword.trim())}
                >
                  {uploadingCatalogue ? 'Uploading...' : 'Upload to Catalogue'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Service Breakdown Dialog */}
      {showServiceBreakdown && serviceBreakdownData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '700px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 20px 0' }}>
              Service Breakdown by Box Weight
            </h3>

            <div style={{
              backgroundColor: '#d1ecf1',
              border: '1px solid #bee5eb',
              color: '#0c5460',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              <strong>Total Boxes: {serviceBreakdownData.totalBoxes}</strong>
            </div>

            {Object.entries(serviceBreakdownData.ranges).map(([key, range]: [string, any]) => (
              <div key={key} style={{
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                    {range.label}
                  </h4>
                  <span style={{
                    backgroundColor: '#28a745',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    {range.count} {range.count === 1 ? 'box' : 'boxes'}
                  </span>
                </div>

                {range.count > 0 && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '8px',
                    marginTop: '8px'
                  }}>
                    {range.boxes.map((box: any, idx: number) => (
                      <div key={idx} style={{
                        padding: '8px',
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}>
                        <strong>Box {box.boxNumber}</strong><br />
                        {box.weightKg} kg ({box.weightLbs} lbs)
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '20px',
              gap: '12px'
            }}>
              <button
                className="button"
                style={{ backgroundColor: '#28a745' }}
                onClick={handleAddServices}
                disabled={addingServices}
              >
                {addingServices ? 'Adding Services...' : 'Add Services to Shipment'}
              </button>
              <button
                className="button secondary"
                onClick={() => {
                  setShowServiceBreakdown(false)
                  setServiceBreakdownData(null)
                }}
                disabled={addingServices}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CUSMA Form Modal - REMOVED (form filling no longer needed) */}
    </div>
  )
}
